import { LEAD_ID_STORAGE_KEY } from '@/pages/registration/constants';
import { getWPNonce } from '@/utils/getWPNonce';
import apiFetch from '@wordpress/api-fetch';
import { z } from 'zod';

export type CreateLeadArgs = {
	cpf: string;
	email: string;
	name: string;
	phone: string;
	type: 'existing' | 'new';
};

const LEAD_SCHEMA = z.object({
	cpf: z.string(),
	created_at: z.coerce.date(),
	email: z.string(),
	has_own_empty_prize: z
		.string()
		.transform((val) => parseInt(val) === 1)
		.nullable(),
	id: z.coerce.number(),
	name: z.string(),
	phone: z.string(),
	prize_won: z.string().nullable(),
	status: z.enum(['no-prize', 'registered-with-prize']),
	type: z.enum(['new', 'existing']),
});

const PRIZE_SCHEMA = z.object({
	created_at: z.coerce.date(),
	deleted_at: z.coerce.date().nullable(),
	id: z.coerce.number(),
	is_active: z.string().transform((val) => parseInt(val) === 1),
	is_empty: z.string().transform((val) => parseInt(val) === 1),
	is_unlimited: z.string().transform((val) => parseInt(val) === 1),
	name: z.string(),
	probability: z.coerce.number(),
	stock: z.coerce.number(),
	total: z.coerce.number(),
	type: z.enum(['new', 'existing']),
});

const REGISTERED_WITH_PRIZE_SCHEMA = z.object({
	lead: LEAD_SCHEMA,
	roulette: z.array(PRIZE_SCHEMA),
	status: z.literal('registered-with-prize'),
});

const NO_PRIZE_LEAD_RESPONSE_SCHEMA = z.object({
	lead: LEAD_SCHEMA,
	roulette: z.array(PRIZE_SCHEMA),
	status: z.literal('no-prize'),
});

const NOT_REGISTERED_LEAD_RESPONSE_SCHEMA = z.object({
	lead: z.null(),
	status: z.literal('not-registered'),
});

const LEAD_RESPONSE_SCHEMA = z.discriminatedUnion('status', [
	REGISTERED_WITH_PRIZE_SCHEMA,
	NO_PRIZE_LEAD_RESPONSE_SCHEMA,
	NOT_REGISTERED_LEAD_RESPONSE_SCHEMA,
]);

export type LeadResponse = z.infer<typeof LEAD_RESPONSE_SCHEMA>;

const CONTENT_SCHEMA = z.object({
	prize: z.string(),
	registration: z.string(),
	roulette: z.string(),
	whatsapp_message: z.string(),
	whatsapp_number: z.string(),
});

export type Content = z.infer<typeof CONTENT_SCHEMA>;

class MaxxApiClient {
	private pathPrefix = '/wp-json/maxx-api';

	constructor() {
		apiFetch.use(apiFetch.createNonceMiddleware(getWPNonce()));
	}

	public async createLead({
		cpf,
		email,
		name,
		phone,
		type,
	}: CreateLeadArgs): Promise<LeadResponse> {
		const body = JSON.stringify({
			cpf,
			email,
			name,
			phone,
			type,
		});

		const response = await apiFetch({
			body,
			method: 'POST',
			path: `${this.pathPrefix}/leads/`,
		});

		const lead = LEAD_RESPONSE_SCHEMA.parse(response);

		if (lead.status !== 'not-registered') {
			localStorage.setItem(LEAD_ID_STORAGE_KEY, lead.lead.id.toString());
		}

		return lead;
	}

	public async getContent(): Promise<Content> {
		return CONTENT_SCHEMA.parse(window?.MAXX_ROULETTE_CONTENT);
	}

	public async getCurrentLead(): Promise<LeadResponse> {
		const lead_id = localStorage.getItem(LEAD_ID_STORAGE_KEY);

		if (!lead_id) {
			return {
				lead: null,
				status: 'not-registered',
			};
		}

		const response = await apiFetch({
			method: 'GET',
			path: `${this.pathPrefix}/leads/${lead_id}`,
		});

		const lead = LEAD_RESPONSE_SCHEMA.parse(response);

		if (lead.status === 'not-registered') {
			localStorage.removeItem(LEAD_ID_STORAGE_KEY);
		}

		return lead;
	}

	public async spinRoulette(): Promise<void> {
		const lead_id = localStorage.getItem(LEAD_ID_STORAGE_KEY);

		if (!lead_id) {
			throw new Error('No lead to spin roulette');
		}

		await apiFetch({
			method: 'POST',
			path: `${this.pathPrefix}/leads/${lead_id}/spin-roulette`,
		});
	}
}

export const maxxApiClient = new MaxxApiClient();
